import "./LoginPage.scss";

import { Link, useNavigate } from 'react-router-dom';
import { Fragment } from 'react/jsx-runtime';
import z from 'zod';
import { useForm } from '../../hooks/Form';
import { useCurrentUser } from '../../UserProvider';
import { api, setAuthToken } from '../../utils/api';
import { useEffect } from "react";
import SsoButtons from "./SsoButtons";
import usePageTitle from "../../hooks/PageTitle";

const schema = z.object({
  email: z.string()
    .nonempty("Email is required")
    .email("Invalid email address"),
  password: z.string()
    .nonempty("Password is required")
    .min(8, "Password must be at least 8 characters")
    .regex(/\d/, "Password must contain at least one number")
    .regex(/[^A-Za-z0-9]/, "Password must contain at least one special character")
});

function createInitialValues() {
  return {
    email: '',
    password: '',
  };
}

function createInitialErrors() {
  return {
    email: null,
    password: null,
  };
}

export default function LoginPage() {
  usePageTitle("Login");

  const navigate = useNavigate();
  const { refreshUser, currentUser } = useCurrentUser();

  const { isLoading, setIsLoading, error, setError, errors, setErrors, setFieldError, values, setFieldValue } = useForm({
    initialValues: createInitialValues(),
    initialErrors: createInitialErrors()
  });


  useEffect(() => {
    if (currentUser) {
      navigate('/home', { replace: true });
    }
  }, [currentUser, navigate]);

  /** @param {React.FormEvent<HTMLFormElement>} event */
  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      setError(null);
      setErrors(createInitialErrors());

      setIsLoading(true);

      const result = schema.safeParse(values);

      if (!result.success) {
        result.error.issues.forEach(err => {
          setFieldError(err.path[0], err.message);
        });

        return;
      }

      const { data } = await api.post(`/auth/login`, {
        email: result.data.email,
        password: result.data.password
      });

      setAuthToken(data);

      await refreshUser();

      navigate('/jobs');

    } catch {
      setError('We didn\'t find an account with those login credentials');

    } finally {
      setIsLoading(false);
    }
  };

  /*useEffect(() => {
    document.title = 'Login - Papaya';
  }, []);
  */
 
  return (
    <Fragment>
      <form onSubmit={handleSubmit} className='form'>
        <h1 className='form__title'>
          Let's get back to work
        </h1>
        <p className='form__subtitle'>
          It's good to see you again
        </p>
        <SsoButtons />
        <div className='form__field-group'>
          <div className='text-field text-field--white form__field'>
            <label htmlFor="email" className='text-field__label'>
              E-mail
            </label>
            <input
              id="email"
              name='email'
              type="text"
              placeholder="example@domain.com"
              value={values.email}
              required
              onChange={(e) => setFieldValue("email", e.target.value)}
              className='text-field__input'
            />
            {errors.email && (
              <p className='text-field__error'>
                {errors.email}
              </p>
            )}
          </div>
          <div className='text-field text-field--white form__field'>
            <label htmlFor="password" className='text-field__label'>
              Password
            </label>
            <input
              id='password'
              name='password'
              type="password"
              placeholder="• • • • • • • •"
              value={values.password}
              required
              onChange={(e) => setFieldValue("password", e.target.value)}
              className='text-field__input'
            />
            {errors.password && (
              <p className='text-field__error'>
                {errors.password}
              </p>
            )}
            <Link to='/password-reset' className='text-field__link'>Forgot your password?</Link>
          </div>
        </div>
        {error && (
          <p className='error error--black'>
            {error}
          </p>
        )}
        <div className='form__button-group'>
          <Link className='button button--white button--outline' to='/signup'>
            Create Account
          </Link>
          <button type='submit' className='button button--white button--fill'>
            {isLoading ? 'Loading...' : 'Login'}
          </button>
        </div>
      </form>
    </Fragment>
  );
}